import React from 'react';
import { useQuery, gql } from '@apollo/client';
import { useParams, useLocation } from 'react-router-dom';
import { BehindFeatureFlag } from '@moda/portal-stanchions';
import { hrefFor } from '../../routers';
import { PageLayoutQuery, PageLayoutQueryVariables } from '../../generated/types/PageLayoutQuery';
import { usePreserveUtmQueryParam } from '../../hooks/usePreserveUtmQueryParam';
import { useSessionQueryParams } from '../../hooks/useSessionQueryParams';
import { usePreferences } from '../../hooks/usePreferences';
import { usePageTracking } from '../../hooks/usePageTracking';
import { useHandleBambuserRedirects } from '../../hooks/useHandleBambuserRedirects';
import { useSignifydSessionId } from '../../hooks/useSignifydSessionId';
import { useCountry } from '../CountrySelector';
import { SignifydScript } from '../SignifydScript';
import { ErrorBoundary } from '../ErrorBoundary';
import { SitePromoBanner, SITE_PROMO_BANNER_FRAGMENT } from '../SitePromoBanner';
import { SiteNav, SITE_NAV_FRAGMENT } from '../SiteNav';
import { MinimalNav } from '../MinimalNav';
import { Notifications } from '../Notifications';
import { SiteFooter } from '../SiteFooter';
import './PageLayout.scss';

export const PAGE_LAYOUT_QUERY = gql`
  query PageLayoutQuery($gender: String!, $slug: String!, $countryCode: String!) {
    displayPage(gender: $gender, slug: $slug, country_code: $countryCode) {
      ...SitePromoBannerFragment
      ...SiteNavFragment
    }
  }
  ${SITE_NAV_FRAGMENT}
  ${SITE_PROMO_BANNER_FRAGMENT}
`;

export const PageLayout: React.FC = ({ children }) => {
  const {
    country: { alpha2Code: countryCode }
  } = useCountry();
  const { vertical } = useParams<{ vertical?: string }>();

  const { pathname } = useLocation();
  const includeHeader = pathname !== hrefFor.CheckoutPage();

  const { vertical: preferredVertical } = usePreferences();
  const gender = vertical && ['men', 'women'].includes(vertical) ? vertical : preferredVertical;

  const { data, error } = useQuery<PageLayoutQuery, PageLayoutQueryVariables>(PAGE_LAYOUT_QUERY, {
    variables: { gender, slug: gender, countryCode },
    skip: !includeHeader
  });

  if (error) {
    // Allow page to render as best it can and warn the error
    console.warn(error.message); // eslint-disable-line no-console
  }

  usePreserveUtmQueryParam();
  useSessionQueryParams();
  usePageTracking();
  useHandleBambuserRedirects();
  useSignifydSessionId({ createIfNotExists: true });

  return (
    <div className="PageLayout">
      <BehindFeatureFlag flagName="signifyd-integration" showWhenValueIs={true}>
        <SignifydScript />
      </BehindFeatureFlag>

      {includeHeader && (
        <ErrorBoundary FallbackComponent={MinimalNav}>
          <SitePromoBanner displayPage={data?.displayPage} />
          <SiteNav displayPage={data?.displayPage} />
        </ErrorBoundary>
      )}

      <Notifications />

      <main className="PageLayout__content">{children}</main>

      <SiteFooter />
    </div>
  );
};
